import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Alert, Box, Stack, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {DefaultPaginationInfo} from '../../../../../../../core/constants/enums';
import {BizPointAdminPurchaseConfirmDataGridColumnNames} from '../../../../../../../core/constants/table/data-grid-column-keys';
import PartnerDataGrid from '../../../../../../containers/data-grid';
import {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from '../../../../../../../packages/data-grid';
import useSearch from '../../../../../../hooks/use-search';
import useIsMounted from '../../../../../../hooks/use-is-mounted';
import {useConfigureNotebook} from '../../../../../../hooks/use-notebook-config';
import useBehavioralAnalyticsTool from '../../../../../../hooks/use-behavioural-analytics-tool';
import useAccessLevels from '../../../../../../hooks/use-access-levels';
import Button from '../../../../../../components/base/button/button';
import SearchInput from '../../../../../../components/base/input/search_input';
import PageHeader from '../../components/page-header';
import {paginateMockItems} from '../../mock-search';
import {mockPendingPurchases} from '../../mock-data';

const dataGridSaveConfig = {name: 'BizPointAdminPurchaseConfirmDataGrid', version: 1};
const col = BizPointAdminPurchaseConfirmDataGridColumnNames;

const searchOptions = {
    keywordFields: ['ref', 'business', 'eftRef'],
    sortFieldMap: {
        [col.ref]: 'ref',
        [col.business]: 'business',
        [col.date]: 'date',
        [col.amount]: 'amt',
        [col.biz]: 'biz',
        [col.eftRef]: 'eftRef',
    },
};

const AdminPurchaseConfirmView = () => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.biz-point.admin.purchaseConfirm'});
    const {t: tSearch} = useTranslation('views', {keyPrefix: 'app-specific.biz-point.common.search'});
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();
    const canExportAdminPurchaseConfirm = operatorAccessLevels.packages.bizPoint.exportAdminPurchaseConfirm;
    const {configureNotebookTags} = useConfigureNotebook('biz-point-admin-purchase-confirm');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const [sourceItems, setSourceItems] = useState(mockPendingPurchases);
    const [data, setData] = useState([]);
    const [confirming, setConfirming] = useState(null);

    /**
     * Configures notebook tags and behavioral analytics for purchase confirm.
     */
    useEffect(() => {
        configureNotebookTags((tags) => tags.bizPointAdminPurchaseConfirm);
        configureBehavioralTool((tags) => tags.bizPointAdminPurchaseConfirm);
    }, [configureBehavioralTool, configureNotebookTags]);

    const columns = useMemo(() => [
        {title: t('ref'), name: col.ref, type: DataGridColumnTypes.string, alignment: DataGridColumnAlignments.left},
        {title: t('business'), name: col.business, type: DataGridColumnTypes.string, alignment: DataGridColumnAlignments.left},
        {title: t('date'), name: col.date, type: DataGridColumnTypes.string, alignment: DataGridColumnAlignments.left},
        {title: t('amount'), name: col.amount, type: DataGridColumnTypes.string, alignment: DataGridColumnAlignments.right},
        {title: t('biz'), name: col.biz, type: DataGridColumnTypes.number, alignment: DataGridColumnAlignments.right},
        {title: t('eftRef'), name: col.eftRef, type: DataGridColumnTypes.string, alignment: DataGridColumnAlignments.left},
        {
            title: t('actions'),
            name: col.actions,
            type: DataGridColumnTypes.element,
            alignment: DataGridColumnAlignments.left,
            sortable: false,
            pinned: true,
            pinnedToggleable: false,
            visibilityToggleable: false,
            width: 120,
        },
    ], [t]);

    const [dataGridColumns, dataGridSortBy, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    const initialPaginationInfo = useMemo(
        () => (dataGridPageSize ? {...DefaultPaginationInfo, pageSize: dataGridPageSize} : DefaultPaginationInfo),
        [dataGridPageSize],
    );

    /**
     * Loads pending purchases from mock data.
     */
    const search = useCallback(async (args) => {
        await Promise.resolve();
        // TODO: Replace with real API when endpoint is available
        const page = paginateMockItems(sourceItems, args, searchOptions);
        if (!isMounted()) {
            return;
        }
        setData(page);
    }, [isMounted, sourceItems]);

    const searchData = useSearch(search, {
        paginationInfo: initialPaginationInfo,
        orderBy: dataGridSortBy,
        filters: {keyword: ''},
    }, true);

    /**
     * Applies keyword filter via useSearch (panel SearchInput pattern).
     */
    const searchByKeyword = useCallback((keyword) => {
        searchData.setFilters({
            keyword: keyword?.toString()?.trim() ?? '',
        });
    }, [searchData]);

    const getExportInfo = useCallback(async () => {
        const items = await Promise.resolve(paginateMockItems(sourceItems, {
            filters: searchData.filters,
            orderBy: searchData.orderBy,
            paginationInfo: {currentPage: 1, pageSize: 100000},
            setPaginationInfo: () => {},
        }, searchOptions));
        if (!items?.length) {
            return undefined;
        }
        const excludeNames = ['actions'];
        const valueResolvers = {
            [col.amount]: (item) => `$${item.amt.toLocaleString()}`,
        };
        const exportColumns = columns.filter((column) => {
            if (!column?.name || excludeNames.includes(column.name)) {
                return false;
            }
            if (column.type === DataGridColumnTypes.element && !valueResolvers[column.name]) {
                return false;
            }
            return true;
        });
        const body = items.map((item) => {
            const row = {};
            for (const column of exportColumns) {
                const header = typeof column.title === 'function'
                    ? column.title()?.toString()
                    : (column.title ?? column.name);
                if (valueResolvers[column.name]) {
                    row[header] = valueResolvers[column.name](item) ?? '--';
                } else {
                    const value = item[column.name];
                    row[header] = value == null || value === '' ? '--' : String(value);
                }
            }
            return row;
        });
        return {title: t('exportTitle'), body};
    }, [columns, searchData.filters, searchData.orderBy, sourceItems, t]);

    /**
     * Confirms EFT payment and credits undistributed Biz via mock API.
     */
    const handleConfirm = useCallback(async (ref) => {
        setConfirming(ref);
        // TODO: Replace with real API when endpoint is available
        const response = await Promise.resolve({resultFlag: true});
        if (!isMounted()) {
            return;
        }
        setConfirming(null);
        if (response?.resultFlag) {
            setSourceItems((prev) => prev.filter((p) => p.ref !== ref));
            searchData.performSearch();
        }
    }, [isMounted, searchData]);

    const rows = useMemo(() => data.map((row) => ({
        key: row.ref,
        data: row,
        cells: {
            [col.ref]: row.ref,
            [col.business]: row.business,
            [col.date]: row.date,
            [col.amount]: `$${row.amt.toLocaleString()}`,
            [col.biz]: row.biz.toLocaleString(),
            [col.eftRef]: row.eftRef,
            [col.actions]:
                <Button
                    variant="contained"
                    size="small"
                    disabled={confirming === row.ref}
                    onClick={() => handleConfirm(row.ref)}
                >
                    {t('confirm')}
                </Button>,
        },
    })), [confirming, data, handleConfirm, t]);

    return (
        <Box>
            <PageHeader title={t('title')}/>

            {sourceItems.length === 0 && (
                <Alert severity="success">{t('allConfirmed')}</Alert>
            )}

            {sourceItems.length > 0 && (
                <>
                    <Box sx={{mb: 3}}>
                        <SearchInput
                            loading={searchData.loading}
                            autoSearch
                            immediateSearch
                            search={searchByKeyword}
                            initialValue={searchData.filters?.keyword ?? undefined}
                            label={tSearch('keyword')}
                        />
                    </Box>

                    <PartnerDataGrid
                        {...dataGridSaveConfig}
                        initialLayoutState={{columns}}
                        columns={dataGridColumns}
                        rows={rows}
                        state={{
                            pagination: searchData.paginationInfo,
                            sortBy: searchData.orderBy,
                            loading: {state: searchData.loading},
                            density: dataGridDensity,
                        }}
                        hideFooterActions
                        onSortByChanged={(_, current) => searchData.setOrderBy?.(current)}
                        onCurrentPageChanged={(_, current) => searchData.setPaginationInfo?.((prev) => ({...prev, ...current}))}
                        onPageSizeChanged={(_, current) => searchData.setPaginationInfo?.((prev) => ({...prev, ...current}))}
                        getExportInfo={canExportAdminPurchaseConfirm ? getExportInfo : undefined}
                    />
                </>
            )}

            <Stack sx={{mt: 2}}>
                <Typography variant="caption" color="text.secondary">{t('offlineFinanceNote')}</Typography>
            </Stack>
        </Box>
    );
};

export default AdminPurchaseConfirmView;
